"use client";

import React from 'react';
import { FaSearch, FaMicrophoneSlash,  FaFileExport } from 'react-icons/fa';
import { IoOptionsOutline, IoStatsChart, IoInformationCircle } from 'react-icons/io5';
import Header from "@/components/header";
import Footer from "@/components/footer";
import packageJson from "@/package.json";

interface SkeletonProps {
  mtid: string;
}

const Skeleton: React.FC<SkeletonProps> = ({ mtid }) => {
  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header - Static */}
      <Header name={"MTMT Statisztika Generátor"} desc={"Személyre szabott statisztikák generálása MTMT publikációkból"} />

      {/* Hero Section - Static */}
      <section className="relative bg-gradient-to-br from-gray-900 to-black text-white">
        <div className="container mx-auto max-w-7xl px-4 py-10 sm:py-14">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <h1 className="text-2xl sm:text-4xl font-bold leading-tight">
                <span className="animate-pulse bg-white/20 rounded h-8 w-64 mb-2 inline-block"></span>
                <span className="text-bme-main"> v{packageJson.version}</span>
              </h1>
              <p className="mt-3 text-white/80 text-sm sm:text-base">
                Comprehensive academic publication analysis
              </p>
            </div>
            <div className="rounded-2xl bg-white/5 border border-white/10 backdrop-blur p-5">
              <div className="grid grid-cols-3 gap-4 text-center">
                <div className="p-3">
                  <div className="text-xl font-bold">Q</div>
                  <div className="text-xs text-white/70">Q</div>
                </div>
                <div className="p-3">
                  <div className="text-xl font-bold">CIT</div>
                  <div className="text-xs text-white/70">CIT</div>
                </div>
                <div className="p-3">
                  <div className="text-xl font-bold">IF</div>
                  <div className="text-xs text-white/70">IF</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-bme-main to-transparent opacity-80" />
      </section>

      <main className="flex-grow container mx-auto px-4 py-6 max-w-7xl">
        <h1 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6 text-center text-gray-800">
          MTMT Statistics - <span className="text-bme-main">v{packageJson.version}</span>
        </h1>

        {/* Author Search - Static */}
        <div className="bg-white/60 backdrop-blur-xl rounded-2xl shadow-xl border border-white/20 p-4 sm:p-6 mb-6">
          <div className="p-0">
            <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4">
              <div className="relative md:flex-[3]">
                <label htmlFor="authorName" className="block text-sm font-medium text-gray-700 mb-2">
                  Author Name
                </label>
                <div className="relative">
                  <input
                    id="authorName"
                    type="text"
                    placeholder="Enter author name..."
                    className="w-full h-12 text-base px-4 rounded-xl bg-white/90 border border-gray-300 focus:outline-none focus:border-bme-main transition duration-300 pr-12"
                    disabled
                  />
                  <button
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 p-2 text-gray-400 rounded-full"
                    disabled
                  >
                    <FaMicrophoneSlash />
                  </button>
                </div>
              </div>
              <div className="relative md:flex-[1]">
                <label htmlFor="mtmtId" className="block text-sm font-medium text-gray-700 mb-2">
                  MTMT ID
                </label>
                <div className="relative">
                  <input
                    id="mtmtId"
                    type="text"
                    value={mtid}
                    className="w-full h-12 text-base px-4 rounded-xl bg-white/90 border border-gray-300 focus:outline-none focus:border-bme-main transition duration-300 pr-10"
                    disabled
                  />
                  <span className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="w-4 h-4 bg-gray-300 rounded animate-pulse"></div>
                  </span>
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-center">
              <button
                className="inline-flex items-center justify-center gap-2 px-16 h-12 bg-gray-300 text-gray-500 rounded-xl cursor-not-allowed text-sm sm:text-base shadow-lg min-w-[350px]"
                disabled
              >
                <FaSearch />
                <span>Search</span>
              </button>
            </div>
          </div>
        </div>

        {/* Statistics Display - Skeleton */}
        <div className="bg-white/90 border-2 shadow-sm border-gray-300/50 rounded-xl p-2 sm:p-3 md:p-5 mb-7 mt-10">
          <div className="relative flex flex-col items-center space-y-2.5 mb-7 bg-gradient-to-b from-white/80 to-white/40 rounded-lg p-2 sm:p-3">
            <div className="absolute top-2 right-2 text-xs sm:text-sm text-gray-500">
              Last update: <span className="animate-pulse bg-gray-200 rounded h-4 w-20 inline-block"></span>
            </div>

            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800 text-center px-2 pt-3 md:pt-0">
              <span className="animate-pulse bg-gray-200 rounded h-8 w-48 inline-block"></span>
            </h2>

            <div className="flex flex-wrap justify-center gap-2 px-2">
              <span className="px-2 sm:px-3 py-1 bg-gray-200 rounded-full text-xs sm:text-sm text-gray-400 animate-pulse">
                <span className="invisible">PhD</span>
              </span>
              <span className="px-2 sm:px-3 py-1 bg-gray-200 rounded-full text-xs sm:text-sm text-gray-400 animate-pulse">
                <span className="invisible">Dr.</span>
              </span>
            </div>

            <div className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm text-gray-500">
              <span className="px-2 py-1 rounded break-all">
                MTMT ID: {mtid}
              </span>
            </div>
          </div>

          {/* Export Button - Static */}
          <div className="relative">
            <div className="flex justify-end mb-4">
              <button className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-gray-600 bg-gray-100/80 rounded-lg cursor-not-allowed">
                <FaFileExport className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                Export
              </button>
            </div>
          </div>

          <div className="text-center mb-3.5 text-xs sm:text-sm text-gray-600 flex flex-wrap items-center justify-center gap-1 sm:gap-2 px-2 sm:px-0">
            <IoInformationCircle className="text-[#5c6cff] text-base sm:text-lg flex-shrink-0" />
            Click on column headers to compare with different levels
          </div>

          {/* Statistics Table - Skeleton */}
          <div className="w-full overflow-x-auto md:overflow-hidden -mx-1.5 sm:mx-0">
            <div className="min-w-[640px] w-full px-1.5 sm:px-0">
              <table className="w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="w-1/6 px-2 py-2 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider">
                    </th>
                    <th className="w-1/6 px-2 py-2 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Current
                    </th>
                    <th className="w-1/6 px-2 py-2 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider">
                      PhD
                    </th>
                    <th className="w-1/6 px-2 py-2 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Habilitation
                    </th>
                    <th className="w-1/6 px-2 py-2 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Level A
                    </th>
                    <th className="w-1/6 px-2 py-2 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Level B
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {Array.from({ length: 11 }).map((_, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-2 py-2 whitespace-normal text-[10px] sm:text-sm font-medium text-gray-900">
                        <span className="animate-pulse bg-gray-200 rounded h-4 w-24 inline-block"></span>
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap text-[10px] sm:text-sm font-bold">
                        <span className="animate-pulse bg-gray-200 rounded h-4 w-12 inline-block"></span>
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap text-[10px] sm:text-sm text-gray-600">
                        <span className="animate-pulse bg-gray-200 rounded h-4 w-8 inline-block"></span>
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap text-[10px] sm:text-sm text-gray-600">
                        <span className="animate-pulse bg-gray-200 rounded h-4 w-8 inline-block"></span>
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap text-[10px] sm:text-sm text-gray-600">
                        <span className="animate-pulse bg-gray-200 rounded h-4 w-8 inline-block"></span>
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap text-[10px] sm:text-sm text-gray-600">
                        <span className="animate-pulse bg-gray-200 rounded h-4 w-8 inline-block"></span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Publications List - Skeleton */}
        <div className="bg-white/60 backdrop-blur-xl shadow-xl rounded-2xl border border-white/20 mt-10">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800 p-3 sm:p-4 bg-white/40 border-b border-white/30 rounded-t-2xl">
            Publications
          </h2>

          <div className="p-4 sm:p-6 bg-white/30 space-y-4 rounded-b-2xl">
            {/* Search */}
            <div className="w-full">
              <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
                Search
              </label>
              <input
                type="text"
                id="search"
                className="w-full p-2 border border-white/40 rounded-lg shadow-sm focus:ring-bme-main focus:border-bme-main bg-white/70"
                placeholder="Search publications..."
                disabled
              />
            </div>

            {/* Simple controls */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="sortOption" className="block text-sm font-medium text-gray-700 mb-1">
                  Sort by
                </label>
                <select
                  id="sortOption"
                  className="w-full p-2 border border-white/40 rounded-lg shadow-sm focus:ring-bme-main focus:border-bme-main bg-white/70"
                  disabled
                >
                  <option>Date</option>
                  <option>Ranking</option>
                  <option>Citations</option>
                </select>
              </div>
              <div className="flex items-end justify-between">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="reverseOrder"
                    className="h-4 w-4 text-bme-main focus:ring-bme-main border-white/40 rounded"
                    disabled
                  />
                  <label htmlFor="reverseOrder" className="ml-2 text-sm text-gray-900">
                    Reverse order
                  </label>
                </div>
                <button
                  type="button"
                  className="group inline-flex items-center gap-2 text-sm px-3 py-2 rounded-lg border border-slate-300/60 text-slate-700 bg-slate-100/60 cursor-not-allowed"
                >
                  <IoOptionsOutline className="text-slate-600" />
                  <span>Show advanced filters</span>
                </button>
              </div>
            </div>

            {/* Summary */}
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Display count: <span className="animate-pulse bg-gray-200 rounded h-4 w-8 inline-block"></span>
              </div>
            </div>
          </div>

          {/* Publications List Items - Skeleton */}
          <ul className="divide-y divide-white/40 mt-4">
            {Array.from({ length: 5 }).map((_, index) => (
              <li key={index} className="p-3 sm:p-4 hover:bg-white/60 transition duration-150 ease-in-out">
                <div className="flex flex-col gap-1.5">
                  {/* Top row: ranking + title, and citations on the right */}
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-2 min-w-0">
                      <span className="px-2 sm:px-3 py-1 text-[10px] sm:text-xs font-semibold rounded-lg text-white shadow flex-shrink-0 bg-gray-300 animate-pulse">
                        <span className="invisible">Q1</span>
                      </span>
                      <h3 className="text-base sm:text-lg font-semibold text-gray-900 leading-snug break-words line-clamp-2">
                        <span className="animate-pulse bg-gray-200 rounded h-5 w-full inline-block"></span>
                      </h3>
                    </div>
                    <span className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-medium text-indigo-700 px-2.5 py-1 bg-indigo-50/70 border border-indigo-200/70 rounded-lg shadow-sm flex-shrink-0">
                      <IoStatsChart className="text-indigo-600" />
                      Citations: <span className="animate-pulse bg-gray-200 rounded h-4 w-6 inline-block"></span>
                    </span>
                  </div>

                  {/* Meta line */}
                  <p className="text-xs sm:text-sm text-gray-600 line-clamp-1">
                    <span className="animate-pulse bg-gray-200 rounded h-4 w-3/4 inline-block"></span>
                  </p>

                  {/* Authors */}
                  <p className="text-[11px] sm:text-xs text-gray-600 line-clamp-1">
                    <span className="animate-pulse bg-gray-200 rounded h-3 w-2/3 inline-block"></span>
                  </p>

                  {/* Badges row */}
                  <div className="flex flex-wrap gap-2 pt-1">
                    <span className="px-2 py-1 bg-white/70 border border-white/40 text-gray-700 text-xs rounded-lg shadow-sm animate-pulse">
                      <span className="invisible">Journal Article</span>
                    </span>
                    <span className="px-2 py-1 bg-white/70 border border-white/40 text-gray-700 text-xs rounded-lg shadow-sm animate-pulse">
                      <span className="invisible">Scientific</span>
                    </span>
                    <span className="px-2 py-1 bg-white/70 border border-white/40 text-gray-700 text-xs rounded-lg shadow-sm animate-pulse">
                      <span className="invisible">MTMT ID: 12345</span>
                    </span>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </main>

      {/* Footer - Static */}
      <Footer />
    </div>
  );
};

export default Skeleton;
